import { Component, computed, DestroyRef, effect, inject, signal, Signal, untracked, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatToolbar } from '@angular/material/toolbar';
import { GoogleDriveService, UploadStatusMessage } from '@iot-platform/core';
import { ExportType } from '@iot-platform/models/grid-engine';
import { DateFormatPipe } from '@iot-platform/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { GridExportService } from '../../../services/grid-export.service';

@Component({
  selector: 'grid-engine-grid-export-dialog',
  templateUrl: './grid-export-dialog.component.html',
  styleUrls: ['./grid-export-dialog.component.scss'],
  imports: [
    FlexLayoutModule,
    TranslatePipe,
    MatCardModule,
    MatToolbar,
    MatProgressBar,
    MatFormFieldModule,
    MatSelect,
    MatOption,
    MatInput,
    ReactiveFormsModule,
    MatButton
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DateFormatPipe]
})
export class GridExportDialogComponent {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly gridExportService: GridExportService = inject(GridExportService);
  private readonly dateFormatPipe: DateFormatPipe = inject(DateFormatPipe);
  private readonly dialogRef: MatDialogRef<GridExportDialogComponent> = inject(MatDialogRef<GridExportDialogComponent>);
  private readonly googleDriveService: GoogleDriveService = inject(GoogleDriveService);
  canUploadToGoogleDrive: Signal<boolean> = this.googleDriveService.canUploadToGoogleDrive;
  googleDriveUploadStatus: Signal<UploadStatusMessage> = this.googleDriveService.uploadStatus;
  progressBarValue: Signal<number> = toSignal(this.gridExportService.progressBarValue$.pipe(takeUntilDestroyed(this.destroyRef)));
  gridExportLoading: Signal<boolean> = toSignal(this.gridExportService.loading$.pipe(takeUntilDestroyed(this.destroyRef)));
  googleDriveLoading: Signal<boolean> = this.googleDriveService.loading;
  loading: Signal<boolean> = computed(() => this.gridExportLoading() || this.googleDriveLoading());
  types: Signal<ExportType[]> = computed(() => {
    const types = [ExportType.CSV, ExportType.XLSX];
    if (this.canUploadToGoogleDrive()) {
      types.push(ExportType.GOOGLE_DRIVE_XLSX);
    }
    return types;
  });
  initialFormState: WritableSignal<string> = signal('');
  canDownload: WritableSignal<boolean> = signal(true);
  data: { totalElements: number; masterView: string } = inject(MAT_DIALOG_DATA);
  defaultType: Signal<ExportType> = computed(() => (this.canUploadToGoogleDrive() ? ExportType.GOOGLE_DRIVE_XLSX : ExportType.XLSX));
  form: UntypedFormGroup = new UntypedFormGroup({
    name: new UntypedFormControl(`${this.time}-${this.data.masterView}`, [Validators.required, Validators.pattern('\\S.*')]),
    type: new UntypedFormControl(this.defaultType(), [Validators.required])
  });
  valueChanges: Signal<any> = toSignal(
    this.form.valueChanges.pipe(
      map(() => ({
        name: this.name?.getRawValue(),
        type: this.type?.getRawValue()
      })),
      takeUntilDestroyed(this.destroyRef)
    )
  );
  onExportComplete: Signal<any> = toSignal(
    this.gridExportService.onExportComplete$.pipe(
      // trigger change detection
      map((complete) => ({ ...this.data, complete })),
      takeUntilDestroyed(this.destroyRef)
    )
  );

  constructor() {
    this.googleDriveService.uploadStatus.set(null);
    this.gridExportService.currentType = this.defaultType();
    this.initialFormState.set(JSON.stringify(this.form.getRawValue()));
    effect(() => {
      const valueChanges = this.valueChanges();
      const googleDriveUploadStatus = this.googleDriveUploadStatus();
      untracked(() => {
        const initialFormState = this.initialFormState();
        const newState: string = JSON.stringify(valueChanges);
        let canDownload = initialFormState !== newState;
        if (valueChanges?.type === ExportType.GOOGLE_DRIVE_XLSX) {
          if (googleDriveUploadStatus === UploadStatusMessage.UploadSuccessful) {
            canDownload = false;
          } else if (
            [
              UploadStatusMessage.AuthenticationFailed,
              UploadStatusMessage.AuthenticationRequired,
              UploadStatusMessage.UnexpectedError,
              UploadStatusMessage.UploadFailed
            ].includes(googleDriveUploadStatus)
          ) {
            canDownload = true;
          }
        }
        this.canDownload.set(canDownload);
        if (canDownload) {
          this.initialFormState.set(newState);
        }
      });
    });

    effect(() => {
      const onExportComplete = this.onExportComplete();
      this.canDownload.set(!onExportComplete || !!onExportComplete?.complete);
      this.name.enable();
      this.type.enable();
    });
  }

  get name(): AbstractControl {
    return this.form.get('name');
  }

  get type(): AbstractControl {
    return this.form.get('type');
  }

  get time(): string {
    return this.dateFormatPipe.transform(Date.now(), 'yyyy-MM-DD-HHmmss');
  }

  onTypeChange({ value }): void {
    this.gridExportService.currentType = value;
    this.name?.setValue(`${this.time}-${this.data.masterView}`);
  }

  submit(): void {
    if (!this.canDownload()) {
      this.close();
    } else if (this.form.valid) {
      this.name.disable();
      this.type.disable();
      this.gridExportService.loadData(this.name.value);
    }
  }

  close(): void {
    this.gridExportService.destroy();
    this.dialogRef.close();
  }
}
